import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Drawer from 'material-ui/Drawer';
import Hidden from 'material-ui/Hidden';
import Typography from 'material-ui/Typography';
import RequestedLoan from './components/RequestedLoan';
import ApplicantsIncome from './components/ApplicantsIncome';
import ApplicantsDetails from './components/ApplicantDetails';
import MortgageType from './components/MortgageType';
import colors from '../../../config/themeColors';
import DrawerDivider from '../../common/components/Drawer/DrawerDivider';

const drawerWidth = 340;

const styles = theme => ({
    root: {
        backgroundColor: colors.white4,
        height: '100%',
        boxShadow: 'inset 8px 0 5px -5px rgba(0, 0, 0, 0.12)',
    },
    center: {
        textAlign: 'center',
    },
    appBar: {
        position: 'absolute',
        marginLeft: drawerWidth,
        [theme.breakpoints.up('md')]: {
            width: `calc(100% - ${drawerWidth}px)`,
        },
    },
    navIconHide: {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    drawerPaper: {
        border: 'none',
        backgroundColor: colors.white3,
        width: drawerWidth,
        zIndex: 1,
        [theme.breakpoints.up('md')]: {
            position: 'relative',
            height: '100%',
        },
    },
    image: {
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        backgroundColor: colors.white2,
        margin: '32px auto',
    },
    heading: {
        fontSize: '32px',
        lineHeight: 1.1,
    },
    headingDesc: {
        fontSize: '22px',
        lineHeight: 1.1,
    },
    headingDescSmaller: {
        fontSize: '14px',
        color: colors.brownishGrey,
        margin: '10px',
    },
    calculator: {
        marginLeft: '24px',
        marginTop: '16px',
    },
    calculatorName: {
        fontSize: '20px',
        color: colors.black,
    },
    calculatorDesc: {
        fontSize: '12px',
        color: colors.warmGrey,
    },
    calculatorProgressContainer: {
        clear: 'both',
        height: '14px',
    },
    calculatorProgress: {
        display: 'inline-block',
        width: '120px',
        backgroundColor: colors.white2,
        borderRadius: '4px',
        height: '6px',
        float: 'left',
        marginTop: '5px',
        marginRight: '15px',
    },
    calculatorProgressDesc: {
        float: 'left',
    },
});

function DrawerComponent(props) {
    const stepsCompleted = props.steps.reduce((prev, next) => prev + next.completed, 0);
    const progressStyle = {
        display: 'inline-block',
        backgroundColor: colors.bloodOrange,
        borderRadius: '4px',
        height: '6px',
        width: `${stepsCompleted * 10}%`,
    };
    const drawer = (
        <div className={props.classes.root}>
            <div className={props.classes.calculator}>
                <div>
                    <Typography className={props.classes.calculatorName}>
                        BTL Calculator Summary
                    </Typography>
                </div>
                <div className={props.classes.calculatorProgressContainer}>
                    <div className={props.classes.calculatorProgress}>
                        <div style={progressStyle}>&nbsp;</div>
                    </div>
                    <div className={props.classes.calculatorProgressDesc}>
                        <Typography className={props.classes.calculatorDesc}>
                            { 10 - stepsCompleted } minutes to go
                        </Typography>
                    </div>
                </div>
            </div>
            { stepsCompleted === 0 &&
            <div className={props.classes.center}>
                <DrawerDivider />
                <div className={props.classes.image}>&nbsp;</div>
                <Typography className={props.classes.heading}>
                    Summary
                </Typography>
                <Typography className={props.classes.headingDesc}>
                    Details
                </Typography>
                <Typography className={props.classes.headingDescSmaller}>
                    A summary of the data you enter <br />
                    will appear here.
                </Typography>
            </div>
            }
            {props.steps[0].completed &&
                <div>
                    <MortgageType
                        mortgageType={props.mortgageType}
                    />
                    <RequestedLoan
                        mortgageType={props.mortgageType}
                        applicantsIncome={props.applicantsIncome}
                        requestedLoanAmount={props.requestedLoanAmount}
                        estaminatedPropertyValue={props.estaminatedPropertyValue}
                        estaminatedPropertyMonthlyRent={props.estaminatedPropertyMonthlyRent}
                        requestedTimeSpan={props.requestedTimeSpan}
                        steps={props.steps}
                        maxLendAmount={props.maxLendAmount}
                        decision={props.decision}
                    />
                    <ApplicantsIncome
                        applicantsIncome={props.applicantsIncome}
                    />
                </div>
            }
            {props.steps[3].completed &&
                <ApplicantsDetails
                    applicant={props.applicant}
                    user={props.user}
                />
            }
        </div>
    );

    return (
        <div className={props.classes.root}>
            <Hidden mdUp>
                <Drawer
                    variant="temporary"
                    anchor={props.theme.direction === 'rtl' ? 'right' : 'left'}
                    open={props.drawerOpened}
                    onClose={() => {
                        props.setDrawerOpened(false);
                    }}
                    classes={{
                        paper: props.classes.drawerPaper,
                    }}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                >
                    {drawer}
                </Drawer>
            </Hidden>
            <Hidden smDown implementation="css">
                <Drawer
                    variant="permanent"
                    open
                    classes={{
                        paper: props.classes.drawerPaper,
                    }}
                >
                    {drawer}
                </Drawer>
            </Hidden>
        </div>
    );
}

DrawerComponent.propTypes = {
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    drawerOpened: PropTypes.bool.isRequired,
    setDrawerOpened: PropTypes.func.isRequired,
    steps: PropTypes.array.isRequired,
    mortgageType: PropTypes.string.isRequired,
    applicantsIncome: PropTypes.array.isRequired,
    requestedLoanAmount: PropTypes.object.isRequired,
    estaminatedPropertyValue: PropTypes.object.isRequired,
    estaminatedPropertyMonthlyRent: PropTypes.object.isRequired,
    requestedTimeSpan: PropTypes.number.isRequired,
    applicant: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    maxLendAmount: PropTypes.object.isRequired,
    decision: PropTypes.string.isRequired,
};

export default withStyles(styles, { withTheme: true })(DrawerComponent);



// WEBPACK FOOTER //
// ./src/app/FormStepper/Drawer/Drawer.js